import { Activity, Brain, Clock, Cpu, Database, Network, Sparkles, TrendingUp, Zap } from "lucide-react";

const STATS = [
  { icon: Brain, label: "Active Agents", value: "1,284", delta: "+12.4%", color: "primary" },
  { icon: Zap, label: "Tokens / sec", value: "42.7K", delta: "+8.1%", color: "secondary" },
  { icon: Database, label: "Memory", value: "14.2 GB", delta: "+0.4%", color: "accent" },
  { icon: TrendingUp, label: "Cost / hr", value: "$2.84", delta: "-23.6%", color: "success" },
];

const RECENT = [
  { agent: "Synth-04", action: "compressed 1,284 memories", time: "12s ago", status: "ok" },
  { agent: "Critic-01", action: "flagged hallucination risk", time: "38s ago", status: "warn" },
  { agent: "Scout-12", action: "spawned 6 sub-agents", time: "1m ago", status: "ok" },
  { agent: "Governance", action: "policy v3.2 enforced", time: "2m ago", status: "ok" },
  { agent: "Self-Heal", action: "auto-recovered tool timeout", time: "4m ago", status: "ok" },
];

export function Dashboard() {
  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">
          Welcome back, <span className="text-gradient-neural">Commander</span>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Your neural mesh is operating at 94% efficiency. 6 universes simulating in parallel.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="glass rounded-xl p-4 relative overflow-hidden group hover:border-primary/40 transition-colors">
              <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-10 group-hover:opacity-20 transition-opacity bg-${s.color}`} style={{ background: `hsl(var(--${s.color}))`, filter: "blur(20px)" }} />
              <div className="flex items-center justify-between mb-3 relative">
                <Icon className={`w-4 h-4`} style={{ color: `hsl(var(--${s.color}))` }} />
                <span className={`text-[10px] font-mono ${s.delta.startsWith("+") ? "text-success" : "text-warning"}`}>
                  {s.delta}
                </span>
              </div>
              <div className="text-2xl font-display font-bold">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 glass rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold">Neural Throughput</h3>
              <p className="text-xs text-muted-foreground">Last 24 hours · live</p>
            </div>
            <div className="flex gap-1.5">
              {["1H", "24H", "7D", "30D"].map((t, i) => (
                <button key={t} className={`text-[10px] font-mono px-2 py-1 rounded ${i === 1 ? "bg-primary/20 text-primary border border-primary/30" : "text-muted-foreground hover:bg-surface-3"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <Sparkline />
        </div>

        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-secondary" />
            <h3 className="font-display font-semibold">AI Insights</h3>
          </div>
          <div className="space-y-3">
            {[
              { tone: "primary", title: "Cost spike predicted", body: "Move Critic agents to Gemini 3.1 Flash → save $84/day." },
              { tone: "secondary", title: "Memory bloat detected", body: "Compress 4.2GB of episodic shards (auto-fix)." },
              { tone: "accent", title: "New tool released", body: "browser.deep-research v2 → +18% accuracy." },
            ].map((i, idx) => (
              <div key={idx} className="rounded-lg border border-border bg-surface-2/40 p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="status-dot" style={{ color: `hsl(var(--${i.tone}))` }} />
                  <span className="text-xs font-display font-semibold">{i.title}</span>
                </div>
                <p className="text-[11px] text-muted-foreground">{i.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-primary" />
            <h3 className="font-display font-semibold">Live Agent Activity</h3>
          </div>
          <div className="space-y-2">
            {RECENT.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-md hover:bg-surface-2/60">
                <span className={`status-dot ${r.status === "ok" ? "text-success" : "text-warning"}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-mono text-primary">{r.agent}</span>
                  <span className="text-xs text-muted-foreground"> · {r.action}</span>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground shrink-0">{r.time}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Network className="w-4 h-4 text-secondary" />
            <h3 className="font-display font-semibold">Topology Health</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { l: "Latency", v: "42ms", g: 92 },
              { l: "Coherence", v: "98%", g: 98 },
              { l: "Drift", v: "0.4σ", g: 85 },
              { l: "Ethics Score", v: "AAA+", g: 99 },
            ].map((m) => (
              <div key={m.l} className="rounded-lg border border-border bg-surface-2/40 p-3">
                <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">{m.l}</div>
                <div className="text-lg font-display font-bold mt-0.5">{m.v}</div>
                <div className="mt-2 h-1 rounded-full bg-surface-3 overflow-hidden">
                  <div className="h-full bg-gradient-neural" style={{ width: `${m.g}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Sparkline() {
  const points = Array.from({ length: 60 }).map((_, i) => {
    const v = 50 + Math.sin(i / 4) * 18 + Math.cos(i / 7) * 12 + Math.random() * 6;
    return { x: i, y: v };
  });
  const max = Math.max(...points.map((p) => p.y));
  const min = Math.min(...points.map((p) => p.y));
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(p.x / 59) * 100} ${100 - ((p.y - min) / (max - min)) * 80 - 10}`)
    .join(" ");
  const area = `${path} L 100 100 L 0 100 Z`;

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-48">
      <defs>
        <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(185 100% 55%)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="hsl(185 100% 55%)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sparkline" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="hsl(185 100% 55%)" />
          <stop offset="50%" stopColor="hsl(270 90% 65%)" />
          <stop offset="100%" stopColor="hsl(320 95% 60%)" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkfill)" />
      <path d={path} stroke="url(#sparkline)" strokeWidth="0.6" fill="none" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
